import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { useSetState } from 'react-use'
import { ApiQueryKeys } from 'api/common/queryKeys'
import { ApplicationEntity } from 'interfaces/applicationEntities/applicationEntity'
import { PurposeDTO } from 'interfaces/purposes/purpose'
import { PERMISSIONS } from 'interfaces/permissions/permissions'
import { RoutesManager } from 'utils/routing/routesManager'
import { useIsPermitted } from 'utils/hooks/useIsPermitted'
import { EditConfirmationModal } from 'components/modals/common/EditConfirmationModal'
import { DeletePurposeModal } from 'components/modals/purposes/DeletePurposeModal'
import { TogglePurposeEnabledStateModal } from 'components/modals/purposes/TogglePurposeEnabledStateModal'
import { Box, Typography } from '@mui/material'
import { formatDateTimeFromUnix } from 'utils/helpers/dateTime'
import { Button, TooltipButton } from '@ketch-com/deck'
import { EmptyValueRenderer } from 'components/EmptyValueRenderer'
import { FirstNameLastNameRenderer } from 'components/renderers/FirstNameLastNameRenderer'

type Props = {
  purpose: PurposeDTO
}

export const PurposeViewActions: React.FC<Props> = ({ purpose }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isPermitted } = useIsPermitted()

  const [{ isEditConfirmationOpen, isStatusOpen, isDeleteOpen }, setState] = useSetState({
    isEditConfirmationOpen: false,
    isStatusOpen: false,
    isDeleteOpen: false,
  })

  const { code, enabled, audit } = purpose
  const isPermittedToManagePurpose = isPermitted(PERMISSIONS.PURPOSE_WRITE)

  const handleRedirectToUpsertPurpose = () => {
    navigate(RoutesManager.policyCenter.purposes.upsert.root.getURL({ code }))
  }

  return (
    <>
      <Box display="flex" alignItems="center" gap="12px">
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="smallBody" color="darkDuskFaded.main">
            Last Updated
          </Typography>
          {audit?.updatedAt ? (
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography color="darkDusk.main" variant="smallBody">
                {formatDateTimeFromUnix(audit?.updatedAt)}
                <FirstNameLastNameRenderer
                  firstName={audit?.updatedBy?.firstName}
                  lastName={audit?.updatedBy?.lastName}
                  renderEmpty={false}
                  prefix="by"
                  textVariant="smallBody"
                />
              </Typography>
            </Box>
          ) : (
            <EmptyValueRenderer />
          )}
        </Box>

        {isPermittedToManagePurpose && (
          <>
            <TooltipButton
              title={enabled ? 'Cannot delete purposes while enabled' : undefined}
              disabled={enabled}
              size="large"
              color="tertiary"
              onClick={() => {
                setState({ isDeleteOpen: true })
              }}
            >
              Delete
            </TooltipButton>
            <Button
              onClick={() => {
                if (enabled) {
                  setState({ isEditConfirmationOpen: true })
                } else {
                  handleRedirectToUpsertPurpose()
                }
              }}
              color="secondary"
              size="large"
            >
              Edit
            </Button>

            <Button
              size="large"
              onClick={() => {
                setState({ isStatusOpen: true })
              }}
            >
              {enabled ? 'Disable' : 'Enable'}
            </Button>
          </>
        )}
      </Box>

      {isStatusOpen && (
        <TogglePurposeEnabledStateModal
          purposeCode={code}
          onSubmit={async () => {
            setState({ isStatusOpen: false })
            await queryClient.refetchQueries([ApiQueryKeys.purpose, { purposeCode: code }])
          }}
          onCancel={() => {
            setState({ isStatusOpen: false })
          }}
        />
      )}

      {isEditConfirmationOpen && (
        <EditConfirmationModal
          entityType={ApplicationEntity.PURPOSE}
          onSubmit={handleRedirectToUpsertPurpose}
          onCancel={() => {
            setState({ isEditConfirmationOpen: false })
          }}
        />
      )}

      {isDeleteOpen && (
        <DeletePurposeModal
          purposeCode={code}
          onSubmit={() => {
            navigate(RoutesManager.policyCenter.purposes.root.getURL())
          }}
          onCancel={() => {
            setState({ isDeleteOpen: false })
          }}
        />
      )}
    </>
  )
}
